import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { appActions } from '../app/slice';
import mime from 'mime';
import { fileService } from 'services/fileService';
import {
  AssignCaseTimestamp,
  CreatePatientMessageReqBody,
  CreatePatinetCaseReqBody,
  PatchPatientCaseReqBody,
  PatientAlignerReqBody,
  PatientTreatmentAndDiagnosisReqBody,
  patientService,
  sendMessagesReactionType,
} from 'services/patientService';
import { RootState } from 'store';
import {
  File,
  GetMessagesAttachmentsRequestParams,
  GetPatientCasesParams,
  GetPatientMessagesRequestParams,
  MessagesType,
  PatientFilesIdByType,
  filesArrType,
  uploadFilesInfoType,
} from 'types';

const basePath = 'patients/';

enum PatientsPaths {
  GET_PATIENT_CASES = 'getPatientCases',
  GET_PATIENT_CASE = 'getPatientCase',
  CREATE_PATIENT_CASE = 'createPatientCase',
  UPDATE_PATIENT_CASE = 'updatePatientCase',
  UPLOAD_PATIENT_FILES = 'uploadPatientFiles',
  GET_PATIENT_CASE_NUMBER = 'getPatientCaseCount',
  ASSIGN_PATIENT_CASE = 'assignPatientCase',
  REMOVE_ASSIGNMENT_FROM_PATIENT_CASE = 'removeAssignmentFromPatientCase',
  UPDATE_PATIENT_DIAGNOSIS_AND_TREATMENT = 'updatePatientTreatmentAndDiagnosis',
  UPDATE_PATIENT_ALIGNER_PLAN = 'updatePatientAlignerPlan',
  GET_ACTIVITY_FEED = 'getActivityFeed',
  GET_PATIENT_MESSAGES = 'getPatientMessages',
  SEND_PATIENT_MESSAGE = 'sendPatientMessage',
  READ_PATIENT_MESSAGE = 'readPatientMessage',
  UPLOAD_MESSAGE_FILES = 'uploadMessageFiles',
  GET_MESSAGE_ATTACHMENTS = 'getMessagesAttachments',
  SEND_MESSAGE_REACTION = 'sendMessageReaction',
  CHANGE_MESSAGES_AFTER_REACTION = 'changedMessagesAfterReaction',
  SET_MESSAGES__AFTER_REACTION = 'setMessagesAfterReaction',
  DELETE_PATIENT_CASE = 'deletePatientCase',
}

type ChangeAssignmentActionType = { assignCaseToDoctorData: AssignCaseTimestamp; patientCaseId: number };

type UpdatePatientCaseActionBodyType = PatchPatientCaseReqBody & {
  clinicId?: string;
};

type UpdatePatientCaseActionType = { patientCaseData: UpdatePatientCaseActionBodyType; patientCaseId: number };
type UpdatePatientTreatmentAndDiagnosisActionType = {
  patientCaseData: PatientTreatmentAndDiagnosisReqBody;
  patientCaseId: number;
};

type UpdatePatientAlignerActionType = {
  patientCaseData: PatientAlignerReqBody;
  patientCaseId: number;
};

type GetPatientMessagesActionType = {
  patientCaseId: number;
  patientMessageParams: GetPatientMessagesRequestParams;
};

type SendPatientMessageActionType = {
  patientCaseId: number;
  messageData: CreatePatientMessageReqBody;
};

type GetMessagesAttachmentsActionType = {
  patientCaseId: number;
  messageAttachmentParams: GetMessagesAttachmentsRequestParams;
};

const getActionName = (path: PatientsPaths) => basePath + path;

const getPatientCases = createAsyncThunk(getActionName(PatientsPaths.GET_PATIENT_CASES), async (_, thunkAPI) => {
  const {
    patients: { patientPageFilters },
  } = thunkAPI.getState() as {
    patients: {
      patientPageFilters: GetPatientCasesParams;
    };
  };
  return await patientService.getPatientCases(patientPageFilters);
});

const getPatientCase = createAsyncThunk(
  getActionName(PatientsPaths.GET_PATIENT_CASE),
  async (patientCaseId: number, thunkAPI) => {
    try {
      return await patientService.getPatientCase(patientCaseId);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.response?.data);
    }
  },
);

const createPatientCase = createAsyncThunk(
  getActionName(PatientsPaths.CREATE_PATIENT_CASE),
  async (newPatientCaseData: CreatePatinetCaseReqBody, thunkAPI) => {
    try {
      const patientCase = await patientService.createPatientCase(newPatientCaseData);
      return thunkAPI.fulfillWithValue({ isCreated: true, patientCase });
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.response?.data);
    }
  },
);

const updatePatientCase = createAsyncThunk(
  getActionName(PatientsPaths.UPDATE_PATIENT_CASE),
  async ({ patientCaseData, patientCaseId }: UpdatePatientCaseActionType, thunkAPI) => {
    try {
      return await patientService.updatePatientCase(patientCaseData, patientCaseId);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.response?.data);
    }
  },
);

const getPatientCaseCount = createAsyncThunk(getActionName(PatientsPaths.GET_PATIENT_CASE_NUMBER), async () => {
  return await patientService.getPatientCaseCount();
});

const assignPatientCaseToDoctor = createAsyncThunk(
  getActionName(PatientsPaths.ASSIGN_PATIENT_CASE),
  async ({ assignCaseToDoctorData, patientCaseId }: ChangeAssignmentActionType, thunkAPI) => {
    try {
      const currentCase = await patientService.assignDoctorToCase(assignCaseToDoctorData, patientCaseId);
      return thunkAPI.fulfillWithValue(currentCase);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

const removeAssignmentFromCase = createAsyncThunk(
  getActionName(PatientsPaths.REMOVE_ASSIGNMENT_FROM_PATIENT_CASE),
  async ({ assignCaseToDoctorData, patientCaseId }: ChangeAssignmentActionType, thunkAPI) => {
    try {
      const currentCase = await patientService.removeAssignment(assignCaseToDoctorData, patientCaseId);
      return thunkAPI.fulfillWithValue(currentCase);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

type UploadPatientFilesProps = {
  newFiles: (File & { data: ArrayBuffer })[];
  patientId: number;
  allPatientFilesIdByType: PatientFilesIdByType;
};

const uploadPatientFiles = createAsyncThunk(
  getActionName(PatientsPaths.UPLOAD_PATIENT_FILES),
  async ({ newFiles, patientId, allPatientFilesIdByType }: UploadPatientFilesProps, thunkAPI) => {
    try {
      const allPatientFilesIdByTypeCopy: PatientFilesIdByType = structuredClone(allPatientFilesIdByType);

      const allFilesUploaded = newFiles.map(async (file) => {
        const requestHeader = mime.getType(file.extension);
        if (!requestHeader) return;
        const { id: s3Id, uploadUrl } = await fileService.getUploadUrl(file.name + '.' + file.extension);
        await fileService.startUploading(uploadUrl, file.data, requestHeader);
        await fileService.finishUploading(s3Id);

        Object.values(allPatientFilesIdByTypeCopy).map((idList) => {
          idList.map((id, index) => {
            if (id === file.id) {
              idList[index] = s3Id;
            }
          });
        });
      });

      await Promise.all(allFilesUploaded);

      await patientService.savePatientCaseAttachedFiles(patientId, allPatientFilesIdByTypeCopy);

      thunkAPI.dispatch(appActions.setIsModalOpened({ name: 'uploadPatientFiles', isOpened: false }));
    } catch (error: any) {
      if (error?.response?.data?.status === 409) {
        return thunkAPI.rejectWithValue(error.response.data.title);
      }
      return thunkAPI.rejectWithValue(error?.message);
    }
  },
);

const updatePatientDiagnosisAndTreatment = createAsyncThunk(
  getActionName(PatientsPaths.UPDATE_PATIENT_DIAGNOSIS_AND_TREATMENT),
  async ({ patientCaseData, patientCaseId }: UpdatePatientTreatmentAndDiagnosisActionType, thunkAPI) => {
    try {
      return await patientService.updatePatientDiagnosisAndTreatment(patientCaseData, patientCaseId);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.response?.data);
    }
  },
);

const updatePatientAlignerPlan = createAsyncThunk(
  getActionName(PatientsPaths.UPDATE_PATIENT_ALIGNER_PLAN),
  async ({ patientCaseData, patientCaseId }: UpdatePatientAlignerActionType, thunkAPI) => {
    try {
      return await patientService.updatePatientAlignerTreatment(patientCaseData, patientCaseId);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.response?.data);
    }
  },
);

const getActivityFeed = createAsyncThunk(getActionName(PatientsPaths.GET_ACTIVITY_FEED), async (_, thunkAPI) => {
  const {
    patients: { activityFeedFilter },
  } = thunkAPI.getState() as {
    patients: {
      activityFeedFilter: GetPatientCasesParams;
    };
  };
  return await patientService.getActivityFeedCases({ ...activityFeedFilter });
});

const getPatientMessages = createAsyncThunk(
  getActionName(PatientsPaths.GET_PATIENT_MESSAGES),
  async ({ patientCaseId, patientMessageParams }: GetPatientMessagesActionType) => {
    return await patientService.getPatientMessages(patientCaseId, patientMessageParams);
  },
);

const sendPatientMessage = createAsyncThunk(
  getActionName(PatientsPaths.SEND_PATIENT_MESSAGE),
  async ({ patientCaseId, messageData }: SendPatientMessageActionType, thunkAPI) => {
    try {
      await patientService.sendPatientMessage(patientCaseId, messageData);
      return thunkAPI.fulfillWithValue({ isSend: true });
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.response?.data);
    }
  },
);

const readPatientMessage = createAsyncThunk(
  getActionName(PatientsPaths.READ_PATIENT_MESSAGE),
  async (patientCaseId: number, thunkAPI) => {
    await patientService.readPatientMessages(patientCaseId);
    return thunkAPI.fulfillWithValue({ isRead: true });
  },
);

const uploadMessageFiles = createAsyncThunk(
  getActionName(PatientsPaths.UPLOAD_MESSAGE_FILES),
  async (files: filesArrType, thunkAPI) => {
    const uploadFilesInfo: uploadFilesInfoType[] = [];

    const allFilesUploaded = files.map(async (file) => {
      const requestHeader = mime.getType(file.extension);
      if (!requestHeader) return;

      const { id: s3Id, uploadUrl } = await fileService.getUploadUrl(file.name + '.' + file.extension);
      await fileService.startUploading(uploadUrl, file.data, requestHeader);
      await fileService.finishUploading(s3Id);
      uploadFilesInfo.push({ id: s3Id, fileExtension: file.extension.toUpperCase() });
    });
    await Promise.all(allFilesUploaded);
    return thunkAPI.fulfillWithValue({ filesInfo: uploadFilesInfo });
  },
);

const getMessagesAttachments = createAsyncThunk(
  getActionName(PatientsPaths.GET_MESSAGE_ATTACHMENTS),
  async ({ patientCaseId, messageAttachmentParams }: GetMessagesAttachmentsActionType) => {
    return await patientService.getMessagesAttachments(patientCaseId, messageAttachmentParams);
  },
);

const sendPatientMessageReaction = createAsyncThunk(
  getActionName(PatientsPaths.SEND_MESSAGE_REACTION),
  async ({ patientCaseId, messageId, reactions }: sendMessagesReactionType) => {
    return await patientService.sendPatientMessageReaction({ patientCaseId, messageId, reactions });
  },
);

const changeReactedMessagesAfterReactionsWithoutRequest = createAsyncThunk(
  getActionName(PatientsPaths.CHANGE_MESSAGES_AFTER_REACTION),
  async (payload: number, { getState, dispatch }) => {
    const state = getState() as RootState;
    let messageIndex = -1;

    const message = state.patients.patientMessage.find((el, index) => {
      if (payload === el.id) {
        messageIndex = index;
        return el;
      }
    });

    if (!message) return;

    const likedMessage: MessagesType = structuredClone(message);

    if (likedMessage.reactions.length) {
      likedMessage.reactions = [];
    } else {
      likedMessage.reactions.push({
        createdBy: '',
        reactions: ['like'],
      });
    }

    const modifiedMessages: MessagesType[] = structuredClone(state.patients.patientMessage);
    modifiedMessages.splice(messageIndex, 1, likedMessage);

    dispatch(setPatientMessagesAfterReactions(modifiedMessages));
  },
);

export const setPatientMessagesAfterReactions = createAction<MessagesType[]>(
  getActionName(PatientsPaths.SET_MESSAGES__AFTER_REACTION),
);

const deletePatientCase = createAsyncThunk(
  getActionName(PatientsPaths.DELETE_PATIENT_CASE),
  async (patientCase: number) => {
    return await patientService.deletePatientCase(patientCase);
  },
);

export const asyncPatientsActions = {
  getPatientCases,
  getPatientCase,
  createPatientCase,
  updatePatientCase,
  getPatientCaseCount,
  assignPatientCaseToDoctor,
  removeAssignmentFromCase,
  uploadPatientFiles,
  updatePatientDiagnosisAndTreatment,
  getActivityFeed,
  getPatientMessages,
  sendPatientMessage,
  readPatientMessage,
  uploadMessageFiles,
  getMessagesAttachments,
  sendPatientMessageReaction,
  changeReactedMessagesAfterReactionsWithoutRequest,
  setPatientMessagesAfterReactions,
  deletePatientCase,
  updatePatientAlignerPlan,
};
